import React, { useState } from 'react';

import { Box } from '@mui/material';
import { GBPCurrency } from '@apigrowfinance/pbolt-icons';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
    LoanItemExpanded,
    RepaymentSchedule,
} from '@clients/domains/lending/lending';
import RepaymentsScheduleListItem from '@components/lending/repaymentsScheduleListItem/RepaymentsScheduleListItem';
import LoadingError from '@components/loadingError/LoadingError';
import { useAccount } from '@contexts/AccountProvider';
import { RepaymentStatus } from '@enums/lending/repaymentStatus';
import useRetrieveAccountLoanDetails from '@hooks/useRetrieveAccountLoanDetails';

const RepaymentsSchedulePanel = () => {
    const { account } = useAccount();
    const { data, isLoading, isError } = useRetrieveAccountLoanDetails({
        expand: true,
    });
    const loanItem = data as LoanItemExpanded;
    const navigate = useNavigate();
    const repayments = loanItem?.repayment_schedule;
    const [isHovered, setIsHovered] = useState(false);
    const viewPaymentsSchedule = () =>
        navigate(`/${account?.account_id}/payments`);

    const today = dayjs();
    const PaidItems = repayments
        ?.filter(
            (payment) =>
                payment.status === 'PAID' ||
                payment.status === 'PROCESSING' ||
                payment.status === 'PAST_DUE',
        )
        .reverse()
        .slice(0, 4);
    const ScheduledItems = repayments
        ?.filter((payment) => dayjs(payment.due_date).isAfter(today))
        .slice(0, 4);

    const hasPaidItems = PaidItems?.length;
    const dataList = hasPaidItems ? PaidItems : ScheduledItems;

    if (isError) {
        return (
            <Panel>
                <PanelContent>
                    <LoadingError />
                </PanelContent>
            </Panel>
        );
    }
    const dummyRows: RepaymentSchedule[] = [];
    const dummyRow: RepaymentSchedule = {
        repayment_id: '4ef76151-dc16-446c-af38-4805be2470c9',
        status: RepaymentStatus.PAID,
        period: 2,
        due_date: '2023-01-10',
        repayment_date: '2022-12-05',
        balance: 9753.9,
        number_of_days: 31,
        auto: false,
        amount_due: {
            principal: 0,
            interest: 0,
            charge: 0,
            tax: 0,
            fee: 0,
            penalty: 0,
            total: 0,
        },
        amount_paid: {
            principal: 123.66,
            interest: 98.78,
            charge: 0,
            tax: 0,
            fee: 0,
            penalty: 0,
            total: 222.44,
        },
        initial_amount: {
            principal: 123.66,
            interest: 98.78,
            charge: 0,
            tax: 0,
            fee: 0,
            penalty: 0,
            total: 222.44,
        },
    };

    const createDummyRow = (repaymentId: string) => {
        return { ...dummyRow, repayment_id: repaymentId };
    };

    dummyRows?.push(createDummyRow('4ef76151-dc16-446c-af38-4805be2470c9'));
    dummyRows?.push(createDummyRow('44545455-dc16-446c-af38-4805be2470c8'));
    dummyRows?.push(createDummyRow('56655656-dc16-446c-af38-4805be2470c7'));
    dummyRows?.push(createDummyRow('89989655-dc16-446c-af38-4805be2470c6'));

    return (
        <Panel
            onClick={isLoading ? null : viewPaymentsSchedule}
            isHovered={setIsHovered}>
            <PanelTitle
                hovered={isHovered}
                loading={isLoading}
                arrowLabel={
                    hasPaidItems ? (
                        <FormattedMessage
                            id="RepaymentsSchedulePanel.viewPastPayments"
                            defaultMessage="View past payments"
                        />
                    ) : (
                        <FormattedMessage
                            id="RepaymentsSchedulePanel.viewUpcoming"
                            defaultMessage="View upcoming payments"
                        />
                    )
                }>
                {hasPaidItems ? (
                    <FormattedMessage
                        id="RepaymentsSchedulePanel.pastPayments"
                        defaultMessage="Past payments"
                    />
                ) : (
                    <FormattedMessage
                        id="RepaymentsSchedulePanel.upcomingPayments"
                        defaultMessage="Upcoming payments"
                    />
                )}
            </PanelTitle>
            <PanelContent>
                {isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                        }}>
                        {dummyRows.map((item: RepaymentSchedule) => {
                            const itemData = {
                                amountDue: item?.amount_due?.total,
                                amountPaid: item.amount_paid?.total,
                                dateDue: item?.due_date,
                                period: item?.period?.toString(),
                                status: item.status,
                            };
                            return (
                                <RepaymentsScheduleListItem
                                    itemData={itemData}
                                    isLoading={isLoading}
                                    key={item.repayment_id}
                                />
                            );
                        })}
                    </Box>
                )}
                {!isLoading && !repayments?.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            flex: '1 1 0',
                            textAlign: 'center',
                        }}>
                        <IllustratedMessage
                            icon={<GBPCurrency color="inherit" />}
                            messageMain={
                                <FormattedMessage
                                    id="RepaymentsSchedulePanel.noPaymentSchedule"
                                    defaultMessage="No repayment schedule"
                                />
                            }
                            messageSub={
                                <FormattedMessage
                                    id="RepaymentsSchedulePanel.noPaymentScheduleFound"
                                    defaultMessage="No repayment schedule was found for this account"
                                />
                            }
                        />
                    </Box>
                )}
                {!isLoading && repayments?.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                        }}>
                        {dataList.map((item: RepaymentSchedule) => {
                            const itemData = {
                                amountDue: item?.amount_due?.total,
                                amountPaid: item.amount_paid?.total,
                                dateDue: item?.due_date,
                                period: item?.period?.toString(),
                                status: item.status,
                            };
                            return (
                                <RepaymentsScheduleListItem
                                    itemData={itemData}
                                    isLoading={isLoading}
                                    key={item.repayment_id}
                                />
                            );
                        })}
                    </Box>
                )}
            </PanelContent>
        </Panel>
    );
};

export default RepaymentsSchedulePanel;
